import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Grid from '@material-ui/core/Grid';

// Project
import { selectState, actionProps } from 'utils/reduxActions';
import { ReactReduxContext } from 'config/configureStore';
import connectSaga from 'utils/saga';
import saga from './saga';

import { clone, truthty, falsy } from 'utils/functions';
import { Container, withStyles } from '@material-ui/core';
import processingPlants, { processingPlantsActions } from './reducer';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CardHeader from '@material-ui/core/CardHeader';
import Menu from '@material-ui/core/Menu';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
// styles
import styles from './styles';
import ProcessingPlantsForm from './components/ProcessingPlantsForm';
import DeleteModal from 'commons/components/DeleteModal';
import MaintainersPaginator from 'commons/components/MaintainersPaginator';

const useStyles = makeStyles(styles);

function ProcessingPlants(props) {
  const { t } = useTranslation();
  const { objects, formIsOpen, formControls, edit, invalidControls, deleteIsOpen, actions } = props;
  const [processingPlantList, setProcessingPlantList] = useState([]);
  const [filter, setFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setProcessingPlantList(
      Object.values(objects.processingSites || {}).filter((item) =>
        item.name.toLowerCase().includes(filter.toLowerCase()),
      ),
    );
  }, [objects.processingSites, filter]);

  useEffect(() => {
    actions.getStateFromApi();
  }, [actions]);

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function toggleForm() {
    actions.toggleForm('formIsOpen');
  }

  function closeForm() {
    actions.toggleForm('deleteIsOpen');
  }

  function toggleDelete() {
    handleClose();
    actions.toggleDelete(anchorEl.dataset.id);
  }

  function toggleEdit() {
    handleClose();
    const item = Object.values(objects.processingSites || {}).find(
      (item) => item.id === Number(anchorEl.dataset.id),
    );
    actions.toggleEdit(item);
  }

  function changeControls(event) {
    actions.changeControls(
      { [event.currentTarget.id]: event.target.value },
      { [event.currentTarget.id]: false },
    );
  }

  function submit() {
    if (falsy(formControls.name)) {
      actions.setInvalidControls({
        name: falsy(formControls.name),
      });
    } else if (edit) {
      actions.editProcessingPlant(props.toEditId, formControls);
    } else {
      actions.createProcessingPlant(formControls);
    }
  }

  function submitDelete() {
    actions.deleteProcessingPlant(props.toDeleteId);
  }

  function onFilterChange(event) {
    setFilter(event.target.value);
  }

  const classes = useStyles();

  const getListItem = (
    processingPlants,
    openMenuHandler,
  ) => (
    <ListItem key={`processingPlant-${processingPlants.id}`}>
      <ListItemAvatar>
        <Avatar className={classes.blue}>{(processingPlants.name || 'p')[0].toUpperCase()}</Avatar>
      </ListItemAvatar>
      <ListItemText primary={processingPlants.name} />
      <ListItemSecondaryAction>
        <IconButton
          data-id={processingPlants.id}
          onClick={openMenuHandler}
          aria-controls={`menu-processingPlant-${processingPlants.id}`}>
          <MoreVertIcon />
        </IconButton>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Container maxWidth={false}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12}>
          <Typography variant="h3" component="h2" className={classes.marginTop}>
            {t('Processing Plants')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardHeader title={t('Filter')} />
            <CardContent>
              <Grid container spacing={3} className={classes.container}>
                <Grid container item xs={4}>
                  <TextField
                    fullWidth
                    id="standard-basic"
                    label={t('Name')}
                    value={filter}
                    onChange={onFilterChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.root}>
            <CardContent>
              {truthty(objects.processingSites) ? (
                <MaintainersPaginator
                  classes={classes}
                  list={processingPlantList}
                  getListItem={getListItem}
                  openMenuHandler={handleClick}
                  closeMenuHandler={handleClose}
                  toggleEdit={toggleEdit}
                  toggleDelete={toggleDelete}
                />
              ) : (
                <Typography variant="body1" component="p">
                  {t('No processing plants found')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Fab color="primary" className={classes.fab} onClick={toggleForm}>
          <AddIcon />
        </Fab>
        <Menu
          id={`menu-processingPlant-${anchorEl?.dataset?.id}`}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}>
          <MenuItem onClick={toggleEdit}>{t('Edit')}</MenuItem>
          <MenuItem onClick={toggleDelete}>{t('Delete')}</MenuItem>
        </Menu>
        <ProcessingPlantsForm
          isOpen={formIsOpen}
          edit={edit}
          controls={formControls}
          invalidControls={invalidControls}
          submit={submit}
          changeControls={changeControls}
          toggleForm={toggleForm}
        />
        <DeleteModal
          isOpen={deleteIsOpen}
          submit={submitDelete}
          toggleForm={closeForm}
          title={t('Delete Processing Plant')}
          message={t('Are you sure you want to delete this processing plant?')}
        />
      </Grid>
    </Container>
  );
}

const withSaga = connectSaga({key: 'processingPlants', saga});
const withConnect = connect(
  selectState(
    'processingPlants.invalidControls',
    'processingPlants.formControls',
    'processingPlants.deleteIsOpen',
    'processingPlants.toDeleteId',
    'processingPlants.formIsOpen',
    'processingPlants.toEditId',
    'processingPlants.edit',
    'app.objects',
  ),
  actionProps(clone(clone.OBJECT, processingPlantsActions)),
  null,
  { context: ReactReduxContext },
);

export default compose(withStyles(styles), withRouter, withConnect)(withSaga(ProcessingPlants));